import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateCombinedMarketCapture, calculateAdjustedChoiceRate } from '../../lib/calculations';
import { formatPercent, formatNumber } from '../../lib/formatters';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { Users } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';

interface CombinedMarketCaptureProps {
  demandMode: 'ceiling' | 'split';
}

export function CombinedMarketCapture({ demandMode }: CombinedMarketCaptureProps) {
  const packages = useSimulatorStore(s => s.packages);
  const featureScaling = useSimulatorStore(s => s.assumptions.featureScaling);
  const portfolio = usePortfolioPnL();
  const isCeiling = demandMode === 'ceiling';

  const combined = useMemo(() =>
    calculateCombinedMarketCapture(
      packages.map(p => ({ price: p.price, enabledFeatures: p.enabledFeatures })),
      featureScaling
    ),
    [packages, featureScaling]
  );

  const firstPkg = packages[0];
  const singleProductRate = calculateAdjustedChoiceRate(firstPkg.price, firstPkg.enabledFeatures, featureScaling);

  return (
    <div className="card card-accent-blue mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} className="text-blue-500" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Combined Market Reach</h3>
        <InfoButton helpKey="combinedMarketReach" size={13} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-xs text-muted mb-1">Ceiling (no cannibalization)</div>
          <div className={`text-2xl font-bold font-mono ${isCeiling ? 'text-primary' : 'text-muted'}`}>{formatPercent(combined.combined)}</div>
          <div className="text-xs text-muted mt-1"><span className="font-mono">{formatNumber(portfolio.totalS2UnitsCeiling)}</span> total S2 units</div>
          <div className="mt-2 space-y-1">
            {combined.perSegment.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <span className="text-secondary">{s.name}</span>
                <span className="font-mono text-primary">{formatPercent(s.maxRate)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted mb-1">Split (proportional sharing)</div>
          <div className={`text-2xl font-bold font-mono ${!isCeiling ? 'text-primary' : 'text-muted'}`}>{formatNumber(Math.round(portfolio.totalS2UnitsSplit))} units</div>
          <div className="text-xs text-muted mt-1">demand shared across packages</div>
          <div className="mt-2 space-y-1">
            {portfolio.packages.map((pkg, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-secondary">{packages[i].name}</span>
                <span className="font-mono text-primary">{formatNumber(Math.round(pkg.s2UnitsSplit))}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted mb-1">Single product baseline ({firstPkg.name}, ${firstPkg.price.toLocaleString()})</div>
          <div className="text-2xl font-bold text-primary font-mono">{formatPercent(singleProductRate)}</div>
          <div className="mt-2 text-xs text-muted">
            {combined.combined > singleProductRate
              ? `+${formatPercent(combined.combined - singleProductRate)} additional market reach with ${packages.length} tiers`
              : 'Adjust tiers to expand market reach beyond the single-product baseline'}
          </div>
        </div>
      </div>

      <p className="text-sm text-muted mt-4 pt-3 border-t border-border leading-relaxed">
        <strong>Ceiling:</strong> Each package captures its full conjoint demand independently (optimistic). <strong>Split:</strong> Per-segment demand is allocated proportionally across packages based on relative attractiveness (conservative). Active mode: <strong>{isCeiling ? 'Ceiling' : 'Split'}</strong> — toggle in the Portfolio P&L panel above.
      </p>
    </div>
  );
}
